import React from 'react';
import { get } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { Observable, EMPTY, of, timer, zip, concat, forkJoin, from, merge } from 'rxjs';
import { map, mergeMap, catchError, takeUntil, throttleTime } from 'rxjs/operators';

import { noop } from 'ui-components/default-props/noop';
import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { messages } from './messages';
import * as Actions from './seller.actions';
import * as SellerApi from './seller.api';
import * as FreightApi from '../../freight/freight-api';
import { Segment } from '../../services/analytics-services/segment';
import { REFRESH_SELECTED_ORDERS_COMPLETE, REFRESH_SELECTED_ORDERS_ERROR } from '../../constants/analytics-events';
import { EventSourceStrings } from '../../tracking/tracking-constants';
import { normalizePusherMessageData } from '../../services/pusher-service/normalize-pusher-message';
import { ShipstationTermsContainer } from '../../components/modals/terms-and-conditions/container/shipstation-terms-container';
import { PhoneVerificationSubject } from '../../components/phone-verification/phone-verificiation-subject';
import { getCountryUIConfig } from '../../contexts/country-config-context/country-ui-config';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { Providers } from '../../enums/providers';
import * as IntegrationsApi from '../../pages/settings/api/integrations-api';
import { getSellerServiceCollectionsCancelablePromise } from '../../pages/settings/api/service-collections';
import { getPlanInfo, manageSubscription } from '../../pages/settings/api/subscription';
import { getSellerIORs } from '../../services/iors/api';
import { getSellerServiceLevels } from '../../services/service-levels/api';
import { LocalStorageService } from '../../services/local-storage-service';
import { LogService } from '../../services/log-service';
import { PackageService } from '../../services/package-service';
import { ProductImportNotificationService } from '../../services/product-import-notification-service';
import { PusherService } from '../../services/pusher-service';
import {
  REFRESH_STORE_INVENTORY,
  REFRESH_STORE_PRODUCTS,
  STORE_REFRESH_STATUS,
} from '../../services/pusher-service/pusher-contract-messages';
import { ShippingServiceService } from '../../services/shipping-service-service';
import { getSellerTaxIdentifiers } from '../../services/tax-identifiers/api';
import * as AnnouncementsActions from '../../store/announcements/announcements.actions';
import { getPartnerSharedServicesSuccess } from '../../store/carrier-sharing/carrier-sharing.actions';
import {
  getPartnerSharedServices,
  getPartnerSharedLabelProviders,
  getPartnerSharedSellersServices,
  getPartnerSharedPackageTypes,
} from '../../store/carrier-sharing/carrier-sharing.api';
import * as CountryActions from '../../store/country/country.actions';
import * as CountryApi from '../../store/country/country.api';
import Global from '../../store/global-selectors';
import { selectLabelQueFromProcessDate, selectLabelQuePageSize } from '../label-que/label-que.selectors';
import * as GridConfigApi from '../../store/grid-config.api';
import * as MapRequestedServiceActions from '../../store/map-requested-service/map-requested-service.actions';
import * as MapRequestedServiceApi from '../../store/map-requested-service/map-requested-service.api';
import * as OrdersFilteringActions from '../../store/orders/filtering/orders.filtering.actions';
import { requestGetOrders, getOrdersCounts } from '../../store/orders/orders.actions';
import { getIsOrdersPage } from '../../store/orders/orders.helpers';
import * as PusherActions from '../../store/pusher/pusher.actions';
import * as ReturnsApi from '../../store/returns/returns.api';
import * as PresetActions from '../../store/seller/presets/seller.presets.actions';
import * as PresetsApi from '../../store/seller/presets/seller.presets.api';
import * as ShippingFilteringActions from '../../store/shipments/filtering/shipments.filtering.actions';
import * as ConnectActions from '../../store/shipstation-connect/ss-connect.actions';
import * as TagsApi from '../../store/tags/tags.api';
import * as UserApi from '../../store/user/user.api';
import { getWalletAndEligibilityDataObservable } from '../../wallet/utilities/wallet-service';
import GuidlessWebSocketRequest from '../../web-socket-request/guidless-web-socket-request';
import { getInventoryWarehouses, getInventoryWarehouseLocations, getAllTotes } from '../inventory/inventory.api';
import { setIORs } from '../iors/index.actions';
import { setServiceLevels } from '../service-levels/service-levels.actions';
import * as LabelQueActions from '../label-que/label-que.actions';
import * as LabelQueApi from '../label-que/label-que.api';
import { getProductProfilesSuccess, getProductCategoriesSuccess } from '../products/products.actions';
import { getProductProfiles, getProductCategoriesAsObservable } from '../products/products.api';
import { getReferenceData } from '../reference-data/reference-data.actions';
import * as ReferenceDataApi from '../reference-data/reference-data.api';
import { setTaxIdentifiers } from '../tax-identifiers/index.actions';

const STORE_REFRESH_STATUS_ERROR = 3;
const STORE_REFRESH_STATUS_SUCCESS = 0;
const shouldIncludeUsage = true;

const PROVIDERS_USING_SERVICE_LEVELS = [Providers.IntersoftRoyalMail.labelProviderId];

export const getSeller = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_SELLER, Actions.SELLER_SUBSCRIPTION_PLAN_INFO_UPDATED),
    mergeMap(() => {
      return SellerApi.getSeller().pipe(
        map((response) => {
          LocalStorageService.setItem('seller_has_created_label', response?.hasCreatedLabel || true);
          return Actions.getSellerSuccess(response);
        }),
        catchError(() => {
          // TODO: handle error case
          return EMPTY;
        }),
      );
    }),
  );
};

export const getSellerLight = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_SELLER_LIGHT),
    mergeMap(() => {
      return SellerApi.getSeller().pipe(
        map((response) => {
          return Actions.getSellerLightSuccess(response);
        }),
        catchError(() => {
          // TODO: handle error case
          return EMPTY;
        }),
      );
    }),
  );
};

export const hydratePackageIdForPackageTypes = (packages) =>
  packages.map((p) => ({
    ...p,
    packageId: PackageService.buildCompositePackageId(p),
  }));

export const mergeServices = (ownServices, sharedServices) => {
  const result = [];
  if (sharedServices?.length > 0) {
    sharedServices.forEach((service) => {
      const isInArray = ownServices.some((object) => object.serviceId === service.serviceId);
      if (!isInArray) {
        result.push(service);
      }
    });
  }
  return result.concat(ownServices);
};

export const mergePackages = (ownPackages, sharedPackages) => {
  const result = [];
  if (sharedPackages?.length > 0) {
    sharedPackages.forEach((packageType) => {
      if (!ownPackages.find((object) => object.packageTypeId === packageType.packageTypeId && object.carrierId === packageType.carrierId)) {
        result.push(packageType);
      }
    });
  }
  return result.concat(ownPackages);
};

export const mergeLabelProviders = (ownLabelProviders, sharedLabelProviders) => {
  const result = [];
  if (sharedLabelProviders?.length > 0) {
    sharedLabelProviders.forEach((labelProvider) => {
      if (
        ownLabelProviders.find((object) => object.sellerLabelProviderId === labelProvider.sellerLabelProviderId) === undefined &&
        result.find((object) => object.sellerLabelProviderId === labelProvider.sellerLabelProviderId) === undefined
      ) {
        result.push(labelProvider);
      }
    });
  }
  return result.concat(ownLabelProviders);
};

const callCarrierSharingApi = (carrierSharedEnabled, apiCall) => {
  if (!carrierSharedEnabled) {
    return from(Promise.resolve(emptyArray));
  }
  return from(apiCall());
};

export const getSellerSuccess = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.GET_SELLER_SUCCESS, Actions.GET_SELLER_DATA),
    mergeMap(() => {
      const state = getState();
      const userId = Global.selectUserCurrentUserId(state);
      const fromProcessDate = selectLabelQueFromProcessDate(state);
      const pageSize = selectLabelQuePageSize(state);
      const sellerId = Global.selectSellerSellerId(state);
      const sellerHomeCountry = Global.selectSellerHomeCountry(state);
      const sellerCurrency = Global.selectSellerDefaultCurrency(state);
      const includeInactiveProviders = true;
      const { isVendorOr3PL } = Global.selectSellerSubscriptionPlanInfo(state);

      return forkJoin(
        SellerApi.getShipFroms(),
        SellerApi.getPackages(),
        SellerApi.getLabelProviders(),
        SellerApi.getServices(),
        SellerApi.getStores(),
        PresetsApi.getPresets(),
        SellerApi.getDeliveryTemplates(),
        SellerApi.getShipmentTemplates(),
        SellerApi.getPackingSlips(),
        UserApi.getUserSettings(),
        TagsApi.getSellerTags(),
        SellerApi.getWorkstations(),
        SellerApi.getPackageSets(),
        SellerApi.getSettings(),
        GridConfigApi.getAllGridConfigs(),
        ReturnsApi.getReturnReasons(),
        SellerApi.getFulfillmentProviders(includeInactiveProviders),
        SellerApi.getFulfillmentProviderServices(),
        LabelQueApi.getLabelQueBatches(userId, fromProcessDate, pageSize),
        getProductProfiles(),
        ReferenceDataApi.getShippingOptions(),
        getProductCategoriesAsObservable(),
        getInventoryWarehouses().promise,
        getInventoryWarehouseLocations().promise.catch((err) => {
          LogService.logInfo('get inventoryLocations failed', { error: err });
        }),
        getWalletAndEligibilityDataObservable(sellerCurrency),
        SellerApi.getSellerBrands(),
        SellerApi.getPendingFulfillmentPartners(),
        IntegrationsApi.getShipperHQConnectedStoreGuids(),
        getSellerServiceCollectionsCancelablePromise(),
        getSellerTaxIdentifiers({ includeInactive: true }),
        callCarrierSharingApi(isVendorOr3PL, getPartnerSharedServices),
        callCarrierSharingApi(isVendorOr3PL, getPartnerSharedLabelProviders),
        callCarrierSharingApi(isVendorOr3PL, getPartnerSharedSellersServices),
        callCarrierSharingApi(isVendorOr3PL, getPartnerSharedPackageTypes),
        CountryApi.getCountryFeatures(sellerHomeCountry),
        getPlanInfo(shouldIncludeUsage).catch(() => emptyObject),
        manageSubscription().catch(() => emptyObject),
        ReferenceDataApi.getFulfillmentOptions(),
        getSellerIORs(),
        from(getSellerServiceLevels().catch(() => emptyArray)),
        FreightApi.getSellerProviders().pipe(catchError(() => of(emptyArray))),
        SellerApi.getHasViewedUpsInvoicedModal().pipe(catchError(() => of(false))),
      ).pipe(
        mergeMap((calls) => {
          const shipFroms = get(calls, '0', emptyArray);
          const stores = get(calls, '4', emptyArray);
          const fulfillmentProviders = get(calls, '16', emptyArray);
          const services = mergeServices(get(calls, '3', emptyArray), get(calls, '32', emptyArray));
          const countryUIConfig = getCountryUIConfig(sellerHomeCountry);
          const sharedCountryConfig = get(calls, '34', emptyArray);
          const countryConfig = [...countryUIConfig, ...sharedCountryConfig];
          const rawSellerFreightProviders = get(calls, '40', emptyArray);
          const sellerFreightProviders = Array.isArray(rawSellerFreightProviders)
            ? rawSellerFreightProviders
            : (rawSellerFreightProviders?.sellerFreightProviders ?? emptyArray);

          const data = {
            shipFroms,
            packages: hydratePackageIdForPackageTypes(mergePackages(get(calls, '1', emptyArray), get(calls, '33', emptyArray))),
            labelProviders: get(calls, '2', emptyArray),
            services: ShippingServiceService.hydrateSellerLabelProviderServices(
              services,
              get(calls, '2', emptyArray),
              get(calls, '20', emptyArray),
            ),
            extendedServices: ShippingServiceService.hydrateSellerLabelProviderServices(
              services,
              mergeLabelProviders(get(calls, '2', emptyArray), get(calls, '31', emptyArray)),
              get(calls, '20', emptyArray),
            ),
            stores,
            presets: get(calls, '5', emptyArray),
            deliveryTemplates: get(calls, '6', emptyArray),
            shipmentTemplates: get(calls, '7', emptyArray),
            packingSlips: get(calls, '8', emptyArray),
            userSettings: get(calls, '9', emptyArray),
            tags: get(calls, '10', emptyArray),
            packageSets: get(calls, '12', emptyArray),
            returnReasons: get(calls, '15', emptyArray),
            settings: get(calls, '13', emptyArray),
            fulfillmentProviders,
            fulfillmentServices: get(calls, '17', emptyArray),
            inventoryWarehouses: get(calls, '22', emptyArray)?.data,
            inventoryWarehousesLocations: get(calls, '23', emptyArray)?.data || emptyArray,
            wallets: get(calls, '24', emptyArray),
            sellerBrands: get(calls, '25', emptyArray),
            pendingFulfillmentPartners: get(calls, '26', emptyArray),
            shipperHQConnectedStores: get(calls, '27', emptyArray),
            serviceCollections: get(calls, '28')?.data ?? emptyArray,
            countryConfig,
            planInfo: get(calls, '35')?.data ?? emptyObject,
            subscriptionData: get(calls, '36')?.data ?? emptyObject,
            fulfillmentProviderOptions: get(calls, '37', emptyArray),
            sellerFreightProviders,
            hasViewedUpsInvoicedModal: get(calls, '41', false),
          };
          const workstations = get(calls, '11', emptyArray);
          const allGridConfigs = get(calls, '14', emptyArray);
          const labelQueBatches = get(calls, '18.pageData', emptyArray);
          const productProfiles = get(calls, '19', emptyArray);
          const productCategories = get(calls, '21', emptyArray);
          const taxIdentifiers = get(calls, '29', emptyArray);
          const iors = get(calls, '38', emptyArray);
          const sharedServices = get(calls, '30', emptyArray);
          const serviceLevels = get(calls, '39', emptyArray);

          return of(
            Actions.getSellerDataSuccess(data),
            setTaxIdentifiers(taxIdentifiers),
            setIORs(iors),
            PresetActions.updatePresetHotkeysSuccess(data.presets),
            ConnectActions.getWorkstationsSuccess(workstations),
            OrdersFilteringActions.getSavedFilterViewsSuccess(allGridConfigs),
            ShippingFilteringActions.getSavedFilterViewsSuccess(allGridConfigs),
            LabelQueActions.getLabelQueBatchesSuccess(labelQueBatches),
            getProductProfilesSuccess(productProfiles),
            getOrdersCounts(),
            getProductCategoriesSuccess(productCategories),
            getPartnerSharedServicesSuccess(sharedServices),
            CountryActions.setCountryConfig(countryConfig),
            setServiceLevels(serviceLevels),
          );
        }),
        catchError((e) => {
          LogService.logInfo('Something is potentially blocking the user from loading the app', {
            sellerId,
            userId,
            message: e?.message ?? '',
            stack: e?.stack ?? '',
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const getAnnouncementsOnGetSellerSuccess = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_SELLER_SUCCESS),
    mergeMap(() => {
      return of(AnnouncementsActions.getAnnouncements());
    }),
  );
};

export const initializeImportNotificationsOnPusherReady = (action$) => {
  return action$.pipe(
    ofType(PusherActions.SELLER_CHANNEL_SUBSCRIBE_SUCCESS),
    mergeMap(() => {
      return of(Actions.productImportNotificationRequested(), Actions.inventoryImportNotificationRequested());
    }),
  );
};

export const getWorkstationsRequest = (action$) => {
  return action$.pipe(
    ofType(ConnectActions.GET_WORKSTATIONS_REQUEST),
    throttleTime(30000),
    mergeMap(() => {
      return SellerApi.getWorkstations().pipe(
        map((workstations) => {
          return ConnectActions.getWorkstationsSuccess(workstations);
        }),
        catchError(() => {
          // TODO: handle error case
          return EMPTY;
        }),
      );
    }),
  );
};

const storeRefreshSuccessNotification$ = (numberOfNewOrUpdatedOrders) => {
  const NOTIFICATION_DURATION = 5000;
  return Observable.create((observer) => {
    const isOrdersPage = getIsOrdersPage();
    const ordersPageGridReloadButton = isOrdersPage
      ? [
          {
            text: <FormattedMessage {...messages.reloadGrid} />,
            theme: 'link',
            callback: () => {
              observer.next(requestGetOrders());
              observer.complete();
            },
          },
        ]
      : [];
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.refreshSuccessTitle} />,
      content: <FormattedMessage {...messages.refreshSuccessBodyControl} values={{ numberOfNewOrUpdatedOrders }} />,
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      duration: NOTIFICATION_DURATION,
      hideNotificationForMobile: true,
      buttons: ordersPageGridReloadButton,
    });
    observer.next(Actions.newOrdersImportedFromRefresh());
  }).pipe(
    mergeMap((next) => of(next)),
    takeUntil(timer(NOTIFICATION_DURATION)),
  );
};

export const refreshStoreRequest = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.REFRESH_STORE_REQUEST),
    throttleTime(3000),
    mergeMap((action) => {
      const state = getState();
      return GuidlessWebSocketRequest({
        callApi: () => SellerApi.refreshStore(action.storeGuid),
        eventNames: STORE_REFRESH_STATUS,
        selectRequestGuids: noop,
        checkForFinalMessage: (pusherMessage) => {
          const data = normalizePusherMessageData(pusherMessage?.data);
          if (data?.Status === STORE_REFRESH_STATUS_SUCCESS || data?.Status === STORE_REFRESH_STATUS_ERROR) {
            return true;
          }
        },
        storeGuids: Global.selectSellerStores(state),
      }).pipe(
        mergeMap((responses) => {
          const store = responses.find((res) => res?.data.Status === STORE_REFRESH_STATUS_SUCCESS);
          const isNotOnboardingPage = window?.location?.pathname !== '/onboard';
          if (store) {
            const numberOfNewOrders = store?.data.NewOrders;
            if (numberOfNewOrders === 0) {
              return of(Actions.newOrdersImportedFromRefresh());
            }
            if (isNotOnboardingPage) {
              return storeRefreshSuccessNotification$(numberOfNewOrders);
            }
          }
          return of(Actions.refreshStoreSuccess());
        }),
      );
    }),
  );

export const refreshAllStoresRequest = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.REFRESH_ALL_STORES_REQUEST),
    throttleTime(3000),
    mergeMap(() => {
      const state = getState();
      const stores = Global.selectSellerStores(state);
      const activeStores = stores.filter((x) => x.isActive && x.marketplaceId !== 0).length;
      let pusherMsgCount = 0;
      return GuidlessWebSocketRequest({
        callApi: () => SellerApi.refreshAllStores(),
        eventNames: STORE_REFRESH_STATUS,
        selectRequestGuids: noop,
        checkForFinalMessage: (pusherMessage) => {
          const data = normalizePusherMessageData(pusherMessage.data);
          if (data?.Status === STORE_REFRESH_STATUS_SUCCESS || data?.Status === STORE_REFRESH_STATUS_ERROR) {
            pusherMsgCount += 1;
          }
          if (activeStores === pusherMsgCount) {
            return true;
          }
        },
        storeGuids: Global.selectSellerStores(state),
      }).pipe(
        mergeMap((response) => {
          const numberOfNewOrders = response.reduce((acc, currentValue) => {
            return acc + (currentValue.data.NewOrders || 0);
          }, 0);
          if (numberOfNewOrders === 0) {
            return of(Actions.refreshAllStoresSuccess(), Actions.newOrdersImportedFromRefresh());
          }
          return storeRefreshSuccessNotification$(numberOfNewOrders);
        }),
      );
    }),
  );

export const getStoreErrorLog = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_STORE_ERROR_LOG),
    mergeMap(({ storeId }) => {
      return SellerApi.getStoreLogs(storeId).pipe(
        map((response) => {
          return Actions.getStoreErrorLogSuccess(response, storeId);
        }),
        catchError((error) => of(Actions.getStoreErrorLogFailure(error))),
      );
    }),
  );
};

export const handleMapRequestedServiceSuccess = (action$) => {
  return action$.pipe(
    ofType(MapRequestedServiceActions.MAP_REQUESTED_SERVICE_SUCCESS),
    mergeMap(({ storeGuid }) => {
      return MapRequestedServiceApi.getMappingsForStore(storeGuid).pipe(
        mergeMap((response) => {
          return of(
            Actions.getServiceMappingsSuccess({
              [storeGuid]: response || emptyArray,
            }),
          );
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const refreshLabelProvidersRequest = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.REFRESH_LABEL_PROVIDERS_REQUEST),
    mergeMap(({ shouldRefreshServices, sellerLabelProviderId }) => {
      const sellerId = get(getState(), 'seller.sellerId');
      return SellerApi.getLabelProviders(sellerId).pipe(
        mergeMap((data) => {
          const actions = [Actions.refreshLabelProvidersSuccess(data)];

          // Conditionally refresh service levels if the provider uses them
          if (sellerLabelProviderId) {
            const updatedProvider = data.find((provider) => provider.sellerLabelProviderId === sellerLabelProviderId);
            if (updatedProvider && PROVIDERS_USING_SERVICE_LEVELS.includes(updatedProvider.labelProviderId)) {
              actions.push(Actions.refreshServiceLevelsRequest());
            }
          }

          if (shouldRefreshServices) {
            actions.push(Actions.refreshShippingServicesRequest());
          }
          return of(...actions);
        }),
        catchError((response) => {
          return of(Actions.refreshLabelProvidersFailure(response));
        }),
      );
    }),
  );
};

export const refreshSellerFreightProvidersRequest = (action$) => {
  return action$.pipe(
    ofType(Actions.REFRESH_SELLER_FREIGHT_PROVIDERS),
    mergeMap(() => {
      return FreightApi.getSellerProviders().pipe(
        map((response) => {
          const sellerFreightProviders = Array.isArray(response) ? response : (response?.sellerFreightProviders ?? []);
          return Actions.refreshSellerFreightProvidersSuccess(sellerFreightProviders);
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const refreshShippingServicesRequest = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.REFRESH_SHIPPING_SERVICES_REQUEST),
    mergeMap(() => {
      return zip(SellerApi.getServices(), ReferenceDataApi.getShippingOptions()).pipe(
        mergeMap(([shippingServices = [], shippingOptions = []]) => {
          const sellerLabelProviders = Global.selectSellerLabelProviders(getState());
          const serviceData = ShippingServiceService.hydrateSellerLabelProviderServices(
            shippingServices,
            sellerLabelProviders,
            shippingOptions,
          );
          return of(Actions.refreshShippingServicesSuccess(serviceData));
        }),
        catchError((res) => {
          return of(Actions.refreshShippingServicesFailure(res));
        }),
      );
    }),
  );
};

export const refreshServiceLevelsRequest = (action$) => {
  return action$.pipe(
    ofType(Actions.REFRESH_SERVICE_LEVELS_REQUEST),
    mergeMap(() => {
      return from(getSellerServiceLevels()).pipe(
        map((data) => setServiceLevels(data)),
        catchError(() => {
          return EMPTY;
        }),
      );
    }),
  );
};

export const refreshPackagesRequest = (action$) => {
  return action$.pipe(
    ofType(Actions.REFRESH_PACKAGES_REQUEST),
    mergeMap(() => {
      return SellerApi.getPackages().pipe(
        mergeMap((data) => {
          const packageData = hydratePackageIdForPackageTypes(data);
          return of(Actions.refreshPackagesSuccess(packageData));
        }),
        catchError((res) => {
          return of(Actions.refreshPackagesFailure(res));
        }),
      );
    }),
  );
};

export const refreshSellerWallets = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.REFRESH_SELLER_WALLETS),
    mergeMap(() => {
      const state = getState();
      const sellerCurrency = Global.selectSellerDefaultCurrency(state);
      return getWalletAndEligibilityDataObservable(sellerCurrency).pipe(
        mergeMap((data) => {
          return of(Actions.updateSellerWallets(data));
        }),
        catchError(() => {
          return of(Actions.refreshSellerWalletsFailure());
        }),
      );
    }),
  );
};

export const promptSellerTermsAndConditionsEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.PROMPT_SHIPSTATION_DHL_TERMS_AND_CONDITIONS),
    mergeMap((action) => {
      const { postAcceptanceAction } = action;
      ModalService.launchModal({
        component: ShipstationTermsContainer,
        props: {
          postAcceptanceAction,
        },
      });
      return EMPTY;
    }),
  );
};

export const acceptTermsAndConditionsEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.ACCEPT_DHL_WALLET_TERMS_REQUEST),
    mergeMap(() => {
      return SellerApi.acceptDhlWalletTerms().pipe(
        mergeMap(() => {
          return of(Actions.acceptDhlWalletTermsSuccess());
        }),
        catchError(() => {
          return of(Actions.acceptDhlWalletTermsFailure());
        }),
      );
    }),
  );
};

export const startStampsAccountVerification = (action$) => {
  return action$.pipe(
    ofType(Actions.STAMPS_ACCOUNT_VERIFICATION),
    mergeMap(({ data = {} }) => {
      const { sellerProviderId: sellerLabelProviderId } = data;
      if (sellerLabelProviderId > 0) {
        PhoneVerificationSubject.next({ sellerLabelProviderId });
      }
      return EMPTY;
    }),
  );
};

export const sellerProvidersUpdated = (action$) => {
  return action$.pipe(
    ofType(Actions.SELLER_PROVIDERS_UPDATED),
    mergeMap(() => {
      return concat(of(Actions.getSellerData()), of(getReferenceData()));
    }),
  );
};

export const refreshStoreList = (action$) => {
  return action$.pipe(
    ofType(Actions.REFRESH_STORE_LIST),
    mergeMap(() => {
      return SellerApi.getStores().pipe(
        mergeMap((stores) => {
          const data = { stores };
          return of(Actions.refreshStoreListSuccess(data));
        }),
        catchError(() => {
          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: (
              <FormattedMessage defaultMessage='There was an issue updating stores.' description='Store refresh error' id='ajTu4u' />
            ),
            duration: 3000,
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const refreshPackingSlipTemplates = (action$) => {
  return action$.pipe(
    ofType(Actions.REFRESH_PACKING_SLIP_TEMPLATES),
    mergeMap(() => {
      return SellerApi.getPackingSlips().pipe(
        map((data) => Actions.refreshPackingSlipTemplatesSuccess(data)),
        catchError(() => EMPTY),
      );
    }),
  );
};

// typeId values from dbo.EmailTemplateType: 0 = shipment, 2 = delivery.
const SHIPMENT_EMAIL_TYPE_ID = '0';
const DELIVERY_EMAIL_TYPE_ID = '2';

const normalizeHtmlTemplate = ({ templateId, name }) => ({ emailTemplateId: templateId, name });

export const refreshDeliveryTemplates = (action$) => {
  return action$.pipe(
    ofType(Actions.REFRESH_DELIVERY_EMAIL_TEMPLATES),
    mergeMap(() => {
      return zip(SellerApi.getDeliveryTemplates(), SellerApi.getHtmlEmailTemplates().pipe(catchError(() => of([])))).pipe(
        map(([legacy, html]) => {
          const legacyIds = new Set(legacy.map((t) => String(t.emailTemplateId)));
          const htmlDelivery = html
            .filter((t) => t.htmlTemplateKey != null)
            .filter((t) => String(t.typeId) === DELIVERY_EMAIL_TYPE_ID)
            .filter((t) => !legacyIds.has(String(t.templateId)))
            .map(normalizeHtmlTemplate);
          return Actions.refreshDeliveryEmailTemplatesSuccess([...legacy, ...htmlDelivery]);
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const refreshShipmentTemplates = (action$) => {
  return action$.pipe(
    ofType(Actions.REFRESH_SHIPMENT_EMAIL_TEMPLATES),
    mergeMap(() => {
      return zip(SellerApi.getShipmentTemplates(), SellerApi.getHtmlEmailTemplates().pipe(catchError(() => of([])))).pipe(
        map(([legacy, html]) => {
          const legacyIds = new Set(legacy.map((t) => String(t.emailTemplateId)));
          const htmlShipment = html
            .filter((t) => t.htmlTemplateKey != null)
            .filter((t) => String(t.typeId) === SHIPMENT_EMAIL_TYPE_ID)
            .filter((t) => !legacyIds.has(String(t.templateId)))
            .map(normalizeHtmlTemplate);
          return Actions.refreshShipmentEmailTemplatesSuccess([...legacy, ...htmlShipment]);
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

// TODO combine this with SellerLight, add in SellerProviders for BYOA and replace kitchen sink getSeller call with this one
export const refreshPlanAndSubscriptionInfo = (action$) => {
  return action$.pipe(
    ofType(Actions.REFRESH_PLAN_AND_SUBSCRIPTION_INFO),
    mergeMap(() => {
      return zip(SellerApi.getPlanInfo(shouldIncludeUsage), SellerApi.manageSubscription()).pipe(
        mergeMap(([planInfoResponse, subscriptionDataResponse]) => {
          return of(
            Actions.updateSellerPlanInfo(planInfoResponse?.data || {}),
            Actions.updateSellerSubscriptionData(subscriptionDataResponse?.data || {}),
          );
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const handleProductImportNotifications = (action$) => {
  return action$.pipe(
    ofType(Actions.PRODUCT_IMPORT_NOTIFICATION_REQUESTED),
    mergeMap(() => {
      return new Observable(() => {
        const unbindFromEvent = PusherService.bindToEvent(REFRESH_STORE_PRODUCTS, (data) => {
          ProductImportNotificationService.processProductImportMessage(data);
        });
        return unbindFromEvent;
      });
    }),
  );
};

export const handleInventoryImportNotifications = (action$) => {
  return action$.pipe(
    ofType(Actions.INVENTORY_IMPORT_NOTIFICATION_REQUESTED),
    mergeMap(() => {
      return new Observable(() => {
        const unbindFromEvent = PusherService.bindToEvent(REFRESH_STORE_INVENTORY, (data) => {
          ProductImportNotificationService.processInventoryImportMessage(data);
        });
        return unbindFromEvent;
      });
    }),
  );
};

/**
 * Helper function to group orders by their store
 * @param {object} state - Redux state
 * @param {string[]} fulfillmentPlanIds - Array of fulfillment plan IDs
 * @returns {object} Object mapping storeGuid to array of fulfillment plan IDs
 */
const groupOrdersByStore = (state, fulfillmentPlanIds) => {
  // Get fulfillment plans for selected orders
  const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds });

  // Get sales order IDs from fulfillment plans
  const salesOrderIds = fulfillmentPlans.flatMap((fp) => fp.salesOrdersFulfilledIds ?? emptyArray);

  // Get sales orders which contain store information
  const salesOrders = Global.selectEntitiesSalesOrdersByIds(state, { salesOrderIds });

  // Create a map of salesOrderId -> storeGuid
  const salesOrderToStoreMap = salesOrders.reduce((acc, so) => {
    const storeGuid = so?.store?.storeGuid;
    if (storeGuid) {
      return { ...acc, [so.salesOrderId]: storeGuid };
    }
    return acc;
  }, {});

  // Group fulfillment plans by their store
  const ordersByStore = fulfillmentPlans.reduce((acc, fp) => {
    // Use first sales order ID - fulfillment plans in this context are 1:1 with sales orders
    // as the UI selects orders (not splits), and each order maps to one store
    const salesOrderId = fp.salesOrdersFulfilledIds?.[0];
    const storeGuid = salesOrderToStoreMap[salesOrderId];
    if (storeGuid) {
      const existingOrders = acc[storeGuid] ?? emptyArray;
      return { ...acc, [storeGuid]: [...existingOrders, fp.fulfillmentPlanId] };
    }
    return acc;
  }, {});

  return ordersByStore;
};

/**
 * Epic to handle refreshing selected orders from their stores
 * Groups orders by store and triggers refresh requests in parallel
 * @param {Observable} action$ - Stream of actions
 * @param {{ getState: () => object }} store - Redux store with getState
 * @returns {Observable} Stream of success/failure actions
 */
export const refreshSelectedOrdersEpic = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.REFRESH_SELECTED_ORDERS_REQUEST),
    mergeMap((action) => {
      const { orderIds, storeGuids } = action;
      const state = getState();

      // Handle edge case where no order IDs provided
      if (!orderIds || orderIds.length === 0) {
        return of(Actions.refreshSelectedOrdersSuccess(emptyArray));
      }

      // If storeGuids not provided, this is the first call from the hook
      // We need to calculate stores and re-dispatch with storeGuids for the reducer
      if (!storeGuids || storeGuids.length === 0) {
        const ordersByStore = groupOrdersByStore(state, orderIds);

        const calculatedStoreGuids = Object.keys(ordersByStore);
        if (calculatedStoreGuids.length === 0) {
          return of(Actions.refreshSelectedOrdersSuccess(emptyArray));
        }

        // Re-dispatch with storeGuids so reducer sets loadingStoreIds
        return of(Actions.refreshSelectedOrders(orderIds, calculatedStoreGuids));
      }

      // Second pass - storeGuids provided, proceed with API calls
      const ordersByStore = groupOrdersByStore(state, orderIds);

      // Handle edge case where no orders are found
      if (Object.keys(ordersByStore).length === 0) {
        return of(Actions.refreshSelectedOrdersSuccess(emptyArray, storeGuids));
      }

      // Create array of refresh requests (one per store) using GuidlessWebSocketRequest
      // The reducer already added storeGuids to loadingStoreIds, so icons should be spinning
      const refreshRequests = Object.entries(ordersByStore).map(([storeGuid, storeOrderIds]) => {
        return GuidlessWebSocketRequest({
          callApi: () => SellerApi.refreshStoreOrders(storeGuid, storeOrderIds),
          eventNames: STORE_REFRESH_STATUS,
          selectRequestGuids: noop,
          checkForFinalMessage: (pusherMessage) => {
            if (
              pusherMessage?.data?.Status === STORE_REFRESH_STATUS_SUCCESS ||
              pusherMessage?.data?.Status === STORE_REFRESH_STATUS_ERROR
            ) {
              return true;
            }
          },
          storeGuids: Global.selectSellerStores(state),
        }).pipe(
          map((responses) => {
            const successResponse = responses.find((res) => res?.data?.Status === STORE_REFRESH_STATUS_SUCCESS);
            if (successResponse) {
              return {
                storeGuid,
                orderIds: storeOrderIds,
                success: true,
                newOrders: successResponse?.data?.NewOrders ?? 0,
              };
            }
            return { storeGuid, orderIds: storeOrderIds, success: false, error: 'Store refresh failed' };
          }),
          catchError((error) => of({ storeGuid, orderIds: storeOrderIds, error: error.message, success: false })),
        );
      });

      // Execute API calls and handle results
      return forkJoin(refreshRequests).pipe(
        mergeMap((results) => {
          // Calculate total orders refreshed across all stores
          // For "refresh selected orders", we care about how many were refreshed, not just new ones
          const totalOrdersRefreshed = results.reduce((sum, result) => {
            // Count successfully refreshed orders
            if (result.success && result.orderIds && result.orderIds.length > 0) {
              return sum + result.orderIds.length;
            }
            return sum;
          }, 0);

          Segment.trackEvent(REFRESH_SELECTED_ORDERS_COMPLETE, {
            source: EventSourceStrings.ORDER_GRID_HEADER_OTHER_ACTIONS_DROPDOWN,
            orderCount: totalOrdersRefreshed,
            storeCount: storeGuids.length,
          });

          // Show notification if we're not on onboarding page and dispatch success action
          const isNotOnboardingPage = window?.location?.pathname !== '/onboard';
          if (isNotOnboardingPage && totalOrdersRefreshed > 0) {
            return merge(
              storeRefreshSuccessNotification$(totalOrdersRefreshed),
              of(Actions.refreshSelectedOrdersSuccess(results, storeGuids)),
            );
          }

          return of(Actions.refreshSelectedOrdersSuccess(results, storeGuids));
        }),
        catchError((error) => {
          Segment.trackEvent(REFRESH_SELECTED_ORDERS_ERROR, {
            source: EventSourceStrings.ORDER_GRID_HEADER_OTHER_ACTIONS_DROPDOWN,
            orderCount: orderIds.length,
            storeCount: storeGuids.length,
            error: error?.message,
          });
          return of(Actions.refreshSelectedOrdersError(error, storeGuids));
        }),
      );
    }),
  );

export const getSellerTotesEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_SELLER_TOTES_REQUEST),
    mergeMap(() =>
      from(getAllTotes().promise).pipe(
        map((totes) => Actions.updateSellerTotes(totes || [])),
        catchError((error) => {
          if (error.response?.status === 403 || error.response?.status === 401) {
            return of(Actions.updateSellerTotes([]));
          }
          return of(Actions.setSellerTotesError(error.message || 'Failed to refresh totes'));
        }),
      ),
    ),
  );
};
